import { Card, EmptyState, PageHeader } from '@catering/ui';
import { Link } from '@tanstack/react-router';
import {
  BadgePercent,
  CreditCard,
  Landmark,
  Mail,
  Percent,
  Receipt,
  Settings as SettingsIcon,
  Shield,
  SlidersHorizontal,
  Store,
  Users,
} from 'lucide-react';
import type { JSX } from 'react';

import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';

interface HubCardDefinition {
  to: string;
  title: string;
  description: string;
  icon: typeof Landmark;
  permission: string;
}

/** `settings-hub/spec.md` — table of tarjetas, links and gating permission. */
const HUB_CARDS: readonly HubCardDefinition[] = [
  {
    to: '/settings/arca-config',
    title: 'Datos Fiscales y ARCA',
    description: 'Emisor, certificados y ambiente para facturar ante ARCA.',
    icon: Landmark,
    permission: 'fiscal.config',
  },
  {
    to: '/settings/fiscal-points-of-sale',
    title: 'PV fiscal (ARCA)',
    description: 'Numeración y comprobantes habilitados por PV fiscal.',
    icon: SettingsIcon,
    permission: 'fiscal.config',
  },
  {
    to: '/settings/points-of-sale',
    title: 'Puntos de venta',
    description: 'Unidades operativas de la empresa, su dirección y el PV fiscal con el que facturan.',
    icon: Store,
    permission: 'settings.manage',
  },
  {
    to: '/settings/billing-concepts',
    title: 'Conceptos de Facturación',
    description: 'Catálogo de conceptos que se asignan a los precios y se facturan.',
    icon: Receipt,
    permission: 'fiscal.config',
  },
  {
    to: '/settings/vat-rates',
    title: 'Alícuotas de IVA',
    description: 'Catálogo de alícuotas que se ofrecen al cargar precios y cuál va por defecto.',
    icon: BadgePercent,
    permission: 'fiscal.config',
  },
  {
    to: '/settings/email',
    title: 'Correo saliente',
    description: 'Servidor de correo con el que el sistema envía invitaciones, avisos y comprobantes.',
    icon: Mail,
    permission: 'email.config',
  },
  {
    to: '/settings/payment-methods',
    title: 'Medios de Pago',
    description: 'Catálogo de medios con los que se registran las cobranzas.',
    icon: CreditCard,
    permission: 'settings.manage',
  },
  {
    to: '/settings/withholding-types',
    title: 'Tipos de Retención',
    description: 'Retenciones que se pueden registrar en una cobranza y su alícuota sugerida.',
    icon: Percent,
    permission: 'settings.manage',
  },
  {
    to: '/settings/system-parameters',
    title: 'Parámetros del Sistema',
    description: 'Valores operativos que rigen todo el back-office.',
    icon: SlidersHorizontal,
    permission: 'settings.read',
  },
  {
    to: '/settings/users',
    title: 'Usuarios',
    description: 'Cuentas internas, invitaciones y roles asignados.',
    icon: Users,
    permission: 'user.read',
  },
  {
    to: '/settings/roles',
    title: 'Roles y Permisos',
    description: 'Matriz de roles y los permisos que otorga cada uno.',
    icon: Shield,
    permission: 'role.read',
  },
];

/**
 * `settings-hub/spec.md` — a card that fails its permission check is
 * omitted entirely (not disabled), same criterion `isNavItemVisible`
 * already applies to the sidebar (design.md §9.3).
 */
export function SettingsHubPage(): JSX.Element {
  const currentUser = useCurrentUser();
  const visibleCards = HUB_CARDS.filter((card) => hasPermission(currentUser.data, card.permission));

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={SettingsIcon}
        title="Configuración"
        description="Accedé a los datos fiscales, parámetros del sistema, usuarios y roles."
      />

      {visibleCards.length === 0 ? (
        <EmptyState
          icon={SettingsIcon}
          title="No tenés acceso a ninguna sección de configuración"
          description="Pedile a un administrador que te otorgue el permiso correspondiente."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visibleCards.map((card) => (
            <Link key={card.to} to={card.to} className="block">
              <Card
                variant="outlined"
                className="flex h-full flex-col gap-3 transition-shadow hover:shadow-md"
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <card.icon aria-hidden="true" className="size-5" />
                </span>
                <div className="flex flex-col gap-1">
                  <h2 className="text-base font-semibold text-slate-900">{card.title}</h2>
                  <p className="text-sm text-slate-500">{card.description}</p>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
